import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Divider, Chip, useMediaQuery } from "@mui/material";
import { CustomButton } from "../../../v2/MUI_custom_component";
import BenefitCard from "../../../v2/Benefits/Beneficiary/Components/BenefitCard";
import FirmDetails from "../../../v2/Benefits/Beneficiary/Components/FirmDetails";
import { Edit, EditOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { getShopInsuranceDetailAction } from "./APIs/action";
import { toast } from "react-toastify";
import IconReusable from "../../../components/IconReusable";
import { useSelector } from "react-redux";

const getBooleanValue = (value) => {
    const isTruthy = value === true || value === "True" || value === "true" || value === "yes";
    const isFalsy = value === false || value === "False" || value === "false" || value === "no" || value === null;
    
    if (isTruthy) {
        return { display: "Yes", normalized: "true" };
    }
    if (isFalsy) {
        return { display: "No", normalized: "false" };
    }
    
    // For non-boolean values, return as-is
    return { display: value, normalized: String(value).toLowerCase() };
};

// Helper function to check field dependencies
const shouldShowField = (field, allFields) => {
    if (!field.dependsOn) return true;
    
    const [depId, depVal] = field.dependsOn.split(":");
    const dependentField = allFields.find(f => f.id === depId);
    if (!dependentField) {
        console.warn(`Dependent field ${depId} not found for field ${field.id}`);
        return false;
    }
    const normalizedFieldValue = getBooleanValue(dependentField.value).normalized;
    const normalizedDepVal = depVal.toLowerCase();
    return normalizedFieldValue === normalizedDepVal;
};


const ShopInsuranceDetails = (data) => {
	const navigate = useNavigate()
	const location = useLocation();
	const isMobile = useMediaQuery('(max-width: 1200px)');
	const [isEditing, setIsEditing] = useState(false);
	const [loader, setLoader] = useState(false);
	const insuranceType = location?.state?.insurance_type;
	const [shopDetails, setShopDetails] = useState([])
	const { benefit_card, selected_firm, shop_details, address_details } = shopDetails;
	const {
		selectedEntity: selectedCompanyEntity,
	  } = useSelector(
		(state) => state.entityReducer
	  );
	const handleEdit = (edit_address,shop_details, address_details) => {
		setIsEditing(true);
		navigate('/beneficiary/add-shop-details', {
			state: {
				shop_data: shop_details,
				isEdit: true,
				insurance_type: insuranceType,
				selectedCompanyEntity: selectedCompanyEntity,
				address_details: address_details,
				address_editable: address_details?.is_editable,
				edit_address: edit_address,
			},
		});
	};
	const getShopInsuranceDetailInfo = async (selectedCompanyEntity) => {
		setLoader(true);
		const { data, error, message } = await getShopInsuranceDetailAction(selectedCompanyEntity);
		setLoader(false);
		if (!error) {
			setShopDetails(data);
		} else {
			toast.error(message);
		}
	};
	useEffect(() => {
		if (insuranceType && selectedCompanyEntity) {
			getShopInsuranceDetailInfo(selectedCompanyEntity);
		} else {
			navigate('/beneficiary');
		}
	}, [insuranceType, selectedCompanyEntity]);

	if (loader) {
		return (
			<Box sx={{ p: { xs: 1.5, md: 3 } }}>
				<Typography variant="body2" color="text.secondary">
					Loading...
				</Typography>
			</Box>
		);
	} 
	return (
		<Box sx={{ p: { xs: 1.5, md: 3 } }}>
			<Grid container spacing={2} sx={{ alignItems: 'stretch', mb: 3 }}>
				<Grid item xs={12} sm={6}>
				<BenefitCard item={benefit_card} sx={{ height: '100%' }} />
				</Grid>
				<Grid item xs={12} sm={6}>
				<Box
					sx={{
						background: '#fff',
						p: 2,
						borderRadius: 3,
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-evenly',
					}}
				>
					{(selected_firm) && selected_firm.length > 0 ? (
					selected_firm.map((detail, index) => (
						<FirmDetails key={detail?.id || index} detail={detail} />
					))
					) : (
					<Typography variant="body2" color="text.secondary">
						No firm details available
					</Typography>
					)}
				</Box>
				</Grid>
			</Grid>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 2, mb: 2  }}>
						{address_details && (
							<>
								<Typography variant="h2">
									{address_details?.header_title}
								</Typography>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4  }}>
									<Typography variant="body1">
										{address_details?.details?.address_line_1} {address_details?.details?.address_line_2}, {address_details?.details?.district_name}, {address_details?.details?.location_name}, {address_details?.details?.state_name} - {address_details?.details?.pincode_id}
									</Typography>
									{address_details?.is_editable && (
										<Box sx={{
											cursor: 'pointer', 
											display: 'flex',
											width: '24px',
											height: '24px', 
											justifyContent: 'center'	
										}}
										onClick={() => handleEdit(true, shop_details?.questions, address_details)}
										>
											<IconReusable
												name="Edit"
												size={isMobile ? 16 : 24}
												color="#995A00"
											/>
										</Box>
									)}
								</Box>
							</>
						)}
					</Box>
				</Grid>
			</Grid>
			<Grid container spacing={4}>
				<Grid item xs={12}>
				<Box sx={{ background: '#fff', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography variant="h2">
						{shop_details?.header_title}
					</Typography>
					{shop_details?.is_editable && (

							<Box sx={{
								cursor: 'pointer', 
								display: 'flex',
								width: '24px',
								height: '24px', 
								justifyContent: 'center'	
							}}
							onClick={() => handleEdit(false, shop_details?.questions, address_details)}
							>
								<IconReusable
									name="Edit"
									size={isMobile ? 16 : 24}
									color="#995A00"
								/>
							</Box>
						
					)}
					</Box>
					<Box>
					{shop_details?.questions.map((section) => {
						return (
						<Box key={section.section} sx={{ display: 'flex', flexDirection: 'column',gap:1, mb: 4}}>
							<Typography  variant="labelD" >
							{section?.section}
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
								{section.fields.map((field) => {
									const allFields = shop_details?.questions?.flatMap(s => s.fields) || [];
										if (!shouldShowField(field, allFields)) {
										return null;
									}
									
									return (
										<Box key={field.id} sx={{ display: (field.type !== 'boolean' && field.type !== 'textfield') ? 'block' : 'flex' , justifyContent: 'space-between', gap:1}}>
											<Typography variant="body1" sx={{ 
												color: (field.type === 'textfield') ? '#666666' : 'inherit', 
												fontWeight: (field.type === 'textfield') ? 600 : 'inherit',
												display: (field.type === 'multi-select-chip' && (!field.value || field.value.length === 0)) ? 'none' : 
														field.type === 'multi-select-chip' ? 'none' : 'block' ,
												width: {xs: '70%', md: 'auto'}
											}}>
												{field?.label}
											</Typography>
											{field.type === 'multi-select-chip' ? (
												field.value && field.value.length > 0 ? (
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1}}>
													{field?.value.map((val, i) => (
														<Typography key={i} variant="body1" sx={{ fontWeight: 600 }}>
															{val}
														</Typography>
													))}
													</Box>
												) : (
													<Typography variant="body1" sx={{ fontWeight: 600 }}>
														Not Available
													</Typography>
												)
											) : (
												<Typography variant="body1" sx={{ 
													fontWeight: 600,
													textAlign: 'right',
													width: {xs: '30%', md: 'auto'}
												}}>
													{field?.type === 'boolean'
														? getBooleanValue(field?.value).display
														: field?.id === 'q7' && field?.value
														? `₹ ${field?.value}`
														: field?.value || 'Not Available'}
												</Typography>
											)}
										</Box>
									);
								})}
							</Box>
						</Box>
						);
					})}
					</Box>
				</Box>
				</Grid>
			</Grid>
		</Box>
	);
};

export default ShopInsuranceDetails;
